import { parseEntityRef } from '@backstage/catalog-model'
import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api'
import { LatestRun, RunHistoryItem } from '@internal/backstage-plugin-veho-backstage-common'

export interface AiReadinessApi {
  /** Latest canonical run per (repository, catalog entity) pair, across all repos. */
  listLatest(): Promise<{ items: LatestRun[] }>

  /** Append-only run history for a single catalog entity, newest first. */
  getEntityHistory(entityRef: string, limit?: number): Promise<{ items: RunHistoryItem[] }>
}

export const aiReadinessApiRef = createApiRef<AiReadinessApi>({
  id: 'plugin.ai-readiness.service',
})

export class AiReadinessClient implements AiReadinessApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi
      fetchApi: FetchApi
    }
  ) {}

  private async baseUrl(): Promise<string> {
    return this.options.discoveryApi.getBaseUrl('ai-readiness')
  }

  async listLatest(): Promise<{ items: LatestRun[] }> {
    const url = `${await this.baseUrl()}/latest`
    const response = await this.options.fetchApi.fetch(url)

    if (!response.ok) {
      throw new Error(`Failed to list latest runs: ${response.status} ${response.statusText}`)
    }
    return response.json()
  }

  async getEntityHistory(entityRef: string, limit?: number): Promise<{ items: RunHistoryItem[] }> {
    const { kind, namespace, name } = parseEntityRef(entityRef)
    const path = `entity/${encodeURIComponent(kind)}/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}/history`
    const query = limit === undefined ? '' : `?limit=${encodeURIComponent(limit)}`
    const url = `${await this.baseUrl()}/${path}${query}`
    const response = await this.options.fetchApi.fetch(url)

    if (!response.ok) {
      throw new Error(`Failed to load run history: ${response.status} ${response.statusText}`)
    }
    return response.json()
  }
}
